<template>
  <div id="cert-detail" v-if="certificateDetail">
    <div class="c-backlink mt-lg-3">
      <b-container class="container--max-lg">
        <router-link class="c-link" to="/certificates">
          <mtg-icon icon="ic-angle-left" viewBox="0 0 14 20"></mtg-icon>
          <span>{{ $t("lang.label_back") }}</span>
        </router-link>
      </b-container>
    </div>
    <b-container class="container--max-750">
      <h1 class="h3 mt-lg-3 c-icon-headline mb-6">
        <b-img v-if="isTraditional" class="mr-4" src="images/layout/illu-traditional-certificate-small.png"></b-img>
        <b-img v-else class="mr-4" src="images/layout/illu-post-quantum-certificate-small.png"></b-img>
        <span>{{ certificateDetail.subjectCommonName }}</span>
      </h1>
      <!--<h6 class="mb-4">{{ $t("lang.label_certificate_validity_graph") }}</h6>-->
      <!-- <horizontal-bar-chart :data="chartdata" :options="chartOptions" />-->
      <!-- commenting for now .. can be enabled once this chart is fixed -->
      <div class="table-responsive mb-5">
        <table class="table mb-0">
          <tbody>
            <tr>
              <th class="certDetailsRow">{{ $t("lang.label_certificate_serialNumber") }}</th>
              <td style="word-break: break-word">{{ certificateDetail.serialNumber }}</td>
            </tr>
            <tr>
              <th class="certDetailsRow">{{ $t("lang.label_certificate_subjectDn") }}</th>
              <td style="word-break: break-word">{{ certificateDetail.subjectDn }}</td>
            </tr>
            <tr>
              <th class="certDetailsRow">{{ $t("lang.label_certificate_issuerDn") }}</th>
              <td style="word-break: break-word">{{ certificateDetail.issuerDn }}</td>
            </tr>
            <tr>
              <th class="certDetailsRow">{{ $t("lang.label_certificate_validNotBefore") }}</th>
              <td>{{ dateFormatter(certificateDetail.validNotBefore) }}</td>
            </tr>
            <tr>
              <th class="certDetailsRow">{{ $t("lang.label_certificate_validNotAfter") }}</th>
              <td>{{ dateFormatter(certificateDetail.validNotAfter) }}</td>
            </tr>
            <tr>
              <th class="certDetailsRow">{{ $t("lang.label_certificate_certUsageType") }}</th>
              <td>{{ certificateDetail.certUsageType }}</td>
            </tr>
            <tr v-if="certificateDetail.certUsageType === 'TLS_SERVER'">
              <th class="certDetailsRow">{{ $t("lang.label_certificate_domainName") }}</th>
              <td>{{ certificateDetail.domainName }}</td>
            </tr>
            <tr v-if="certificateDetail.certUsageType === 'SMIME'">
              <th class="certDetailsRow">{{ $t("lang.label_certificate_email") }}</th>
              <td>{{ certificateDetail.email }}</td>
            </tr>
            <tr>
              <th class="certDetailsRow">{{ $t("lang.label_certificate_sha256Fingerprint") }}</th>
              <td style="word-break: break-word">{{ certificateDetail.fingerprint }}</td>
            </tr>
            <tr>
              <th class="certDetailsRow">{{ $t("lang.label_certificate_publicKeyAlgorithm") }}</th>
              <td>{{ certificateDetail.certPublicKeyAlgorithm }}</td>
            </tr>
            <tr>
              <th class="certDetailsRow">{{ $t("lang.label_certificate_signatureAlgorithm") }}</th>
              <td>{{ getSignatureAlgorithmDisplayValue }}</td>
            </tr>
            <tr>
              <th class="certDetailsRow">{{ $t("lang.label_certificate_certStatus") }}</th>
              <td :class="getVariant()">{{ getStatusDisplayValue }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="text-right mb-6">
        <b-button class="btn--has-icon mb-3" variant="primary" @click="downloadCertificate()">
          <mtg-icon icon="ic-download" viewBox="0 0 24 24"></mtg-icon>
          <span>{{ $t("lang.label_certificate_download") }}</span>
        </b-button>
        <b-button class="btn--has-icon mb-3 ml-3" variant="primary" @click="revokeCertificate()">
          <mtg-icon icon="ic-lock" viewBox="0 0 20 24"></mtg-icon>
          <span>{{ $t("lang.label_certificate_revoke_button") }}</span>
        </b-button>
        <b-button class="btn--has-icon mb-3 ml-3" variant="primary" @click="createSimilarCert()">
          <mtg-icon icon="ic-duplicate" viewBox="0 0 22 22"></mtg-icon>
          <span>{{ $t("lang.label_certificate_createSimilar") }}</span>
        </b-button>
      </div>
      <view-certificate-audit :certificateId="certificateId"></view-certificate-audit>
    </b-container>
  </div>
</template>
<style scoped>
.certDetailsRow {
  width: 35%;
}
</style>

<script>
import API from "@/api";
import * as moment from "moment/moment";
import ViewCertificateAudit from "../../components/ViewCertificateAudit";
import CertificateCryptoType from "../../api/enums/CertificateCryptoType";
import * as utils from "@/utils";

export default {
  data() {
    return {
      certificateId: 0,
      certificateDetail: null,
      commonName: "",
      getVariant() {
        let status = this.certificateDetail.certStatus;
        if (status === "ACTIVE") {
          return "text-success font-weight-bold";
        } else if (status === "REVOKED") {
          return "text-warning font-weight-bold";
        } else if (status === "EXPIRED") {
          return "text-danger font-weight-bold";
        }
      }
    };
  },
  created() {
    this.certificateId = this.$route.params.id;
    this.getCertDetails();
  },
  filters: {
    makeReadable(value) {
      if (value === "TLS_SERVER") {
        return "TLS SERVER";
      } else if (value === "TLS_CLIENT") {
        return "TLS CLIENT";
      } else if (value === "SMIME") {
        return "S/MIME";
      }
    }
  },
  methods: {
    getCertDetails() {
      this.$store.dispatch("startLoading");
      API.getCertificateDetail(this.certificateId)
        .then(response => {
          this.$store.dispatch("stopLoading");
          this.certificateDetail = response.data;
        })
        .catch(() => {
          this.$store.dispatch("stopLoading");
          this.$store.dispatch("alert/showFailureAlert", this.$t("lang.message_certificate_load_error"));
        });
    },
    createSimilarCert() {
      const issuedCerts = this.statistics.data.certsIssued;
      if (issuedCerts.issued === issuedCerts.max) {
        this.$bvModal.show("modal-noIssuedCerts");
      } else {
        this.$router.push({ name: "certificate-copy", params: { id: this.certificateId } });
      }
    },
    downloadCertificate() {
      this.$store.dispatch("startLoading");
      API.downloadCertificate(this.certificateId)
        .then(response => {
          this.$store.dispatch("stopLoading");
          utils.downloadCertFile(this.certificateDetail.subjectCommonName + ".pem", response.data.pemCertificate);
        })
        .catch(() => {
          this.$store.dispatch("stopLoading");
          this.$store.dispatch("alert/showFailureAlert", this.$t("lang.message_certificate_download_error_message"));
        });
    },
    revokeCertificate() {
      this.$bvModal
        .msgBoxConfirm(this.$t("lang.message_certificate_revoke_confirmation_message"))
        .then(value => {
          if (value) {
            this.$store.dispatch("startLoading");
            API.revokeCertificate(this.certificateId)
              .then(response => {
                this.$store.dispatch("stopLoading");
                this.certificateDetail = response.data;
                if (response.data.certStatus === "REVOKED") {
                  this.$store.dispatch("alert/showSuccessAlert", this.$t("lang.message_certificate_revoke_success_message"));
                }
                //reload page
                this.$router.go();
              })
              .catch(e => {
                this.$store.dispatch("stopLoading");
                if (e.response.data.message === "Certificate is already revoked or expired.") {
                  this.$store.dispatch("alert/showFailureAlert", this.$t("lang.message_certificate_already_revoked_expired_message"));
                } else {
                  this.$store.dispatch("alert/showFailureAlert", this.$t("lang.message_certificate_revoke_error_message"));
                }
              });
          }
        })
        .catch(() => {});
    },
    dateFormatter(value) {
      return moment(value).format(utils.constants.DATE_PATTERN);
    }
  },
  computed: {
    isTraditional() {
      return this.certificateDetail && this.certificateDetail.certCryptoType === CertificateCryptoType.TRADITIONAL;
    },
    getSignatureAlgorithmDisplayValue() {
      // get label key only when data available , otherwise throws warning in the console.
      if (this.certificateDetail && this.certificateDetail.certSignatureAlgorithm) {
        return this.$t("lang.enum_signatureAlgorithm_" + this.certificateDetail.certSignatureAlgorithm);
      }
      return "";
    },
    getStatusDisplayValue() {
      return utils.toCamelCase(this.certificateDetail.certStatus);
    },
    statistics() {
      return this.$store.getters["certificate/certificateStatistics"]; // we need to get statistics for Issued Certificates
    }
  },
  components: {
    ViewCertificateAudit
  }
};
</script>
